import { useMemo, useState, type ReactNode } from 'react'
import { motion, AnimatePresence, type Variants } from 'framer-motion'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Crown,
  Trash2,
  Loader2,
  Edit3,
  Search,
  UserPlus,
  Check,
  Users as UsersIcon,
  Briefcase,
  Sparkles,
  FolderOpen,
  ArrowRight,
  ShieldCheck,
  CalendarDays,
  X,
  PlusCircle,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { format } from 'date-fns'
import {
  useGetTeamDetailQuery,
  useAssignTeamMembersMutation,
  useRemoveTeamMembersMutation,
  type TeamListItem,
} from '@/redux/service/teams'
import { useGetEmployeesQuery, type Employee } from '@/redux/service/employee'
import { useGetUserQuery } from '@/redux/service/userManagement'
import { initialsFor, avatarColor } from './teamUtils'
import TeamFormDialog from './TeamFormDialog'
import { cn } from '@/lib/utils'

interface TeamDetailDialogProps {
  teamId: number | null
  onClose: () => void
}

const TeamDetailDialog = ({ teamId, onClose }: TeamDetailDialogProps) => {
  const open = teamId !== null
  const { data, isLoading, isFetching } = useGetTeamDetailQuery(teamId!, {
    skip: !open,
  })
  const [assignMembers, { isLoading: isAssigning }] =
    useAssignTeamMembersMutation()
  const [removeMembers, { isLoading: isRemoving }] =
    useRemoveTeamMembersMutation()

  const { data: usersData } = useGetUserQuery(
    { page: 1, limit: 500 },
    { skip: !open }
  )
  const { data: employeesData } = useGetEmployeesQuery(
    { page: 1, limit: 500, salarySetup: 1 },
    { skip: !open }
  )

  const [view, setView] = useState<'detail' | 'add'>('detail')
  const [editOpen, setEditOpen] = useState(false)

  const team = data?.data

  const employeeByUserId = useMemo(() => {
    const map = new Map<number, Employee>()
    employeesData?.data?.forEach(e => map.set(e.user_id, e))
    return map
  }, [employeesData])

  const userByEmail = useMemo(() => {
    const map = new Map<string, { id: number; role_name: string }>()
    usersData?.data?.forEach(u => {
      if (u.email) map.set(u.email.toLowerCase(), u)
    })
    return map
  }, [usersData])

  const aggregatedSkills = useMemo(() => {
    const counts = new Map<string, number>()
    team?.employees?.forEach(m => {
      const e = employeeByUserId.get(m.user_id)
      e?.skills?.forEach(s => counts.set(s, (counts.get(s) ?? 0) + 1))
    })
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1])
  }, [team, employeeByUserId])

  const totalMembers =
    (team?.users?.length ?? 0) + (team?.employees?.length ?? 0)
  const initials = initialsFor(team?.name ?? '')
  const colorClass = avatarColor(team?.name ?? '')
  const leaderName = team?.leader_user_name || team?.leader_employee_name
  const leaderRole = team?.leader_user_email ? 'Lead' : null
  const leaderUserId = team?.leader_user_id ?? null

  const handleRemoveUser = async (userId: number) => {
    if (!teamId) return
    try {
      await removeMembers({ id: teamId, user_ids: [userId] }).unwrap()
      toast.success('User removed from team')
    } catch (err) {
      const error = err as { data?: { message?: string } }
      toast.error(error?.data?.message ?? 'Failed to remove user')
    }
  }

  const handleRemoveEmployee = async (userId: number) => {
    if (!teamId) return
    try {
      await removeMembers({ id: teamId, employee_ids: [userId] }).unwrap()
      toast.success('Employee removed from team')
    } catch (err) {
      const error = err as { data?: { message?: string } }
      toast.error(error?.data?.message ?? 'Failed to remove employee')
    }
  }

  const handleAddEmployee = async (userId: number) => {
    if (!teamId) return
    try {
      await assignMembers({ id: teamId, employee_ids: [userId] }).unwrap()
      toast.success('Member added')
    } catch (err) {
      const error = err as { data?: { message?: string } }
      toast.error(error?.data?.message ?? 'Failed to add member')
    }
  }

  const editTeam: TeamListItem | null = team
    ? {
        id: team.id,
        name: team.name,
        folder_ids: team.folder_ids,
        folders: team.folders ?? [],
        leader_user_id: team.leader_user_id,
        leader_user_name: team.leader_user_name,
        leader_user_email: team.leader_user_email,
        leader_employee_id: team.leader_employee_id,
        leader_employee_name: team.leader_employee_name,
        leader_employee_email: team.leader_employee_email,
        project_manager_id: team.project_manager_id,
        project_manager_name: team.project_manager_name,
        project_manager_email: team.project_manager_email,
        is_active: team.is_active,
        user_member_count: team.users?.length ?? 0,
        employee_member_count: team.employees?.length ?? 0,
        project_count: team.projects?.length ?? 0,
        created_at: team.created_at,
        updated_at: team.updated_at,
      }
    : null

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  }

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 15 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' },
    },
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={o => {
          if (!o) {
            setView('detail')
            onClose()
          }
        }}
      >
        <DialogContent className='!max-w-[1240px] w-[96vw] h-[94vh] p-0 bg-background border-border overflow-hidden flex flex-col gap-0 rounded-3xl shadow-2xl'>
          {/* Header Section */}
          <div className='relative shrink-0 overflow-hidden'>
            <div className='absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-primary/5 pointer-events-none' />
            <DialogHeader className='px-8 py-8 border-b border-border/50 bg-card/30 backdrop-blur-md relative z-10'>
              <div className='w-full flex items-center gap-8'>
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className={cn(
                    'h-20 w-20 rounded-[2.5rem] flex items-center justify-center text-xl font-black shrink-0 shadow-xl border-4 border-background group hover:scale-105 transition-all duration-500',
                    colorClass
                  )}
                >
                  {initials}
                </motion.div>
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center gap-3 flex-wrap'>
                    <DialogTitle className='text-3xl font-black tracking-tighter text-foreground'>
                      {team?.name ?? 'Workspace Team'}
                    </DialogTitle>
                    <AnimatePresence>
                      {team && (
                        <motion.span
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          className={cn(
                            'text-[10px] font-black uppercase tracking-[0.1em] px-3 py-1 rounded-full shadow-sm border',
                            team.is_active === false
                              ? 'text-muted-foreground bg-muted border-border'
                              : 'text-primary bg-primary/10 border-primary/20'
                          )}
                        >
                          {team.is_active === false
                            ? 'Deactivated'
                            : 'Operational'}
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </div>
                  <div className='mt-2 flex items-center gap-4 text-sm font-medium'>
                    {leaderName ? (
                      <div className='flex items-center gap-2 px-3 py-1 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-700 shadow-sm'>
                        <Crown className='w-4 h-4 fill-amber-500/20' />
                        <span>
                          Directed by{' '}
                          <span className='font-bold'>{leaderName}</span>
                        </span>
                        {leaderRole && (
                          <span className='opacity-60 font-black'>
                            · {leaderRole}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className='flex items-center gap-2 px-3 py-1 rounded-xl bg-muted border border-border text-muted-foreground'>
                        <UsersIcon className='w-4 h-4' />
                        <span>No Strategic Leader Assigned</span>
                      </div>
                    )}

                    {team?.project_manager_name && (
                      <div className='flex items-center gap-2 px-3 py-1 rounded-xl bg-blue-500/10 border border-blue-500/20 text-blue-700 shadow-sm'>
                        <ShieldCheck className='w-4 h-4 text-blue-500' />
                        <span>
                          PM:{' '}
                          <span className='font-bold'>
                            {team.project_manager_name}
                          </span>
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className='flex gap-2 shrink-0'>
                  <Button
                    type='button'
                    variant='outline'
                    size='lg'
                    onClick={() => setEditOpen(true)}
                    className='h-12 px-6 rounded-2xl border-border bg-background hover:bg-primary/5 hover:text-primary hover:border-primary/20 font-bold gap-2.5 shadow-sm transition-all'
                  >
                    <Edit3 className='w-4 h-4' />
                    Edit Configuration
                  </Button>
                  <Button
                    type='button'
                    size='icon'
                    variant='outline'
                    onClick={() => onClose()}
                    className='h-12 w-12 rounded-2xl border-border bg-background hover:bg-muted font-bold transition-all shadow-sm'
                  >
                    <X className='w-5 h-5' />
                  </Button>
                </div>
              </div>
            </DialogHeader>
          </div>

          <div className='flex-1 overflow-y-auto  bg-muted/10'>
            <AnimatePresence mode='wait'>
              {isLoading || isFetching || !team ? (
                <motion.div
                  key='loading'
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className='flex flex-col items-center justify-center py-48 gap-4'
                >
                  <div className='relative'>
                    <div className='h-12 w-12 rounded-full border-4 border-primary/20 border-t-primary animate-spin' />
                    <div className='absolute inset-0 flex items-center justify-center'>
                      <UsersIcon className='w-4 h-4 text-primary animate-pulse' />
                    </div>
                  </div>
                  <p className='text-sm font-bold text-muted-foreground tracking-widest uppercase'>
                    Syncing Data...
                  </p>
                </motion.div>
              ) : view === 'add' ? (
                <motion.div
                  key='add-members'
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.02 }}
                  className='p-8'
                >
                  <AddMembersPanel
                    teamMemberIds={
                      new Set(team.employees?.map(e => e.user_id) ?? [])
                    }
                    employees={employeesData?.data ?? []}
                    onAdd={handleAddEmployee}
                    isAssigning={isAssigning}
                    onDone={() => setView('detail')}
                    memberCount={totalMembers}
                  />
                </motion.div>
              ) : (
                <motion.div
                  key='detail'
                  variants={containerVariants}
                  initial='hidden'
                  animate='visible'
                  className='px-8 py-8 space-y-8'
                >
                  {/* High-level Stats */}
                  <motion.div
                    variants={itemVariants}
                    className='grid grid-cols-2 md:grid-cols-4 gap-6'
                  >
                    <StatCard
                      icon={<UsersIcon className='w-5 h-5' />}
                      label='Active Members'
                      value={totalMembers}
                      tint='bg-blue-500/10 text-blue-600 border-blue-500/20'
                    />
                    <StatCard
                      icon={<Briefcase className='w-5 h-5' />}
                      label='Assigned Projects'
                      value={team.projects?.length ?? 0}
                      tint='bg-purple-500/10 text-purple-600 border-purple-500/20'
                    />
                    <StatCard
                      icon={<Sparkles className='w-5 h-5' />}
                      label='Specialized Skills'
                      value={aggregatedSkills.length}
                      tint='bg-emerald-500/10 text-emerald-600 border-emerald-500/20'
                    />
                    <StatCard
                      icon={<FolderOpen className='w-5 h-5' />}
                      label='Workspace Folders'
                      value={team.folders?.length ?? 0}
                      tint='bg-amber-500/10 text-amber-600 border-amber-500/20'
                    />
                  </motion.div>

                  {/* Analysis Grid */}
                  <motion.div
                    variants={itemVariants}
                    className='grid grid-cols-1 lg:grid-cols-3 gap-8'
                  >
                    <div className='lg:col-span-2 rounded-[2rem] border border-border/50 bg-card p-8 shadow-sm relative overflow-hidden group'>
                      <div className='absolute top-0 right-0 p-8 opacity-[0.03] group-hover:opacity-[0.07] transition-opacity'>
                        <Sparkles className='w-32 h-32 text-emerald-500' />
                      </div>
                      <div className='flex items-center justify-between mb-8'>
                        <div className='flex items-center gap-3'>
                          <div className='h-10 w-10 rounded-2xl bg-emerald-500/10 flex items-center justify-center text-emerald-600'>
                            <Sparkles className='w-5 h-5' />
                          </div>
                          <div>
                            <p className='text-lg font-black tracking-tight text-foreground'>
                              Core Competencies
                            </p>
                            <p className='text-xs text-muted-foreground font-bold uppercase tracking-widest mt-0.5'>
                              {aggregatedSkills.length} Verified Skills
                            </p>
                          </div>
                        </div>
                      </div>
                      {aggregatedSkills.length > 0 ? (
                        <div className='flex flex-wrap gap-2'>
                          {aggregatedSkills.map(([skill, count]) => (
                            <motion.span
                              key={skill}
                              whileHover={{ y: -2, scale: 1.05 }}
                              className='inline-flex items-center gap-2.5 text-xs px-4 py-2 rounded-2xl bg-muted border border-border hover:border-primary/30 hover:bg-primary/5 font-bold transition-all shadow-sm'
                            >
                              {skill}
                              <span className='h-5 min-w-[20px] px-1.5 flex items-center justify-center rounded-lg bg-primary/10 text-[10px] text-primary font-black'>
                                {count}
                              </span>
                            </motion.span>
                          ))}
                        </div>
                      ) : (
                        <div className='py-12 border-2 border-dashed border-border rounded-[1.5rem] text-center'>
                          <Sparkles className='w-8 h-8 text-muted-foreground/30 mx-auto mb-3' />
                          <p className='text-sm font-bold text-muted-foreground'>
                            No skills detected in this unit
                          </p>
                        </div>
                      )}
                    </div>

                    <div className='rounded-[2rem] border border-border/50 bg-card p-8 shadow-sm relative overflow-hidden group'>
                      <div className='absolute top-0 right-0 p-8 opacity-[0.03] group-hover:opacity-[0.07] transition-opacity'>
                        <FolderOpen className='w-32 h-32 text-amber-500' />
                      </div>
                      <div className='flex items-center gap-3 mb-8'>
                        <div className='h-10 w-10 rounded-2xl bg-amber-500/10 flex items-center justify-center text-amber-600'>
                          <FolderOpen className='w-5 h-5' />
                        </div>
                        <div>
                          <p className='text-lg font-black tracking-tight text-foreground'>
                            Integration
                          </p>
                          <p className='text-xs text-muted-foreground font-bold uppercase tracking-widest mt-0.5'>
                            {team.folders?.length ?? 0} Associated Folders
                          </p>
                        </div>
                      </div>
                      {team.folders && team.folders.length > 0 ? (
                        <div className='space-y-3'>
                          {team.folders.map(f => (
                            <div
                              key={f.folder_id}
                              className='flex items-center gap-3 p-3 rounded-2xl bg-muted/50 border border-border/50 group-hover:bg-card group-hover:border-amber-500/30 transition-all'
                            >
                              <div className='h-8 w-8 rounded-lg bg-amber-500/10 flex items-center justify-center text-amber-600 shrink-0'>
                                <Check className='w-4 h-4' />
                              </div>
                              <div className='flex flex-col min-w-0'>
                                <span className='text-xs font-black truncate'>
                                  {f.folder_name}
                                </span>
                                <span className='text-[10px] text-muted-foreground/60 font-mono'>
                                  ID: {f.folder_id}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className='py-12 border-2 border-dashed border-border rounded-[1.5rem] text-center'>
                          <FolderOpen className='w-8 h-8 text-muted-foreground/30 mx-auto mb-3' />
                          <p className='text-sm font-bold text-muted-foreground'>
                            No external folder links
                          </p>
                        </div>
                      )}
                    </div>
                  </motion.div>

                  {/* Members Registry */}
                  <motion.div
                    variants={itemVariants}
                    className='rounded-[2.5rem] border border-border/50 bg-card shadow-xl overflow-hidden'
                  >
                    <div className='flex items-center justify-between p-8 border-b border-border/50 bg-muted/20'>
                      <div className='flex items-center gap-4'>
                        <div className='h-12 w-12 rounded-2xl bg-blue-500/10 flex items-center justify-center text-blue-600 shadow-inner'>
                          <UsersIcon className='w-6 h-6' />
                        </div>
                        <div>
                          <h3 className='text-xl font-black tracking-tight text-foreground'>
                            Resource Registry
                          </h3>
                          <p className='text-xs text-muted-foreground font-bold uppercase tracking-widest mt-0.5'>
                            {totalMembers} Assigned Members
                          </p>
                        </div>
                      </div>
                      <Button
                        type='button'
                        onClick={() => setView('add')}
                        className='h-12 px-6 bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_8px_16px_-6px_rgba(var(--primary),0.3)] hover:shadow-[0_8px_20px_-4px_rgba(var(--primary),0.4)] transition-all duration-300 rounded-2xl font-bold flex items-center gap-2.5 group'
                      >
                        <UserPlus className='w-4 h-4 group-hover:scale-110 transition-transform' />
                        Onboard Member
                      </Button>
                    </div>

                    {totalMembers === 0 ? (
                      <div className='py-32 text-center bg-muted/5'>
                        <div className='inline-flex h-20 w-20 items-center justify-center rounded-[2rem] bg-muted/50 text-muted-foreground/30 mb-6 rotate-6'>
                          <UsersIcon className='w-10 h-10' />
                        </div>
                        <p className='text-lg font-black text-foreground'>
                          Empty Registry
                        </p>
                        <p className='text-sm text-muted-foreground max-w-xs mx-auto mt-2'>
                          This team currently possesses no assigned human
                          resources.
                        </p>
                        <Button
                          variant='link'
                          onClick={() => setView('add')}
                          className='mt-6 font-bold text-primary'
                        >
                          Start Onboarding Process{' '}
                          <ArrowRight className='ml-2 h-4 w-4' />
                        </Button>
                      </div>
                    ) : (
                      <div className='overflow-x-auto '>
                        <table className='w-full border-collapse'>
                          <thead>
                            <tr className='text-[10px] font-black uppercase tracking-[0.15em] text-muted-foreground bg-muted/10 border-b border-border/50'>
                              <th className='text-left py-4 px-8'>
                                Member Profile
                              </th>
                              <th className='text-left py-4 px-4'>
                                System Role
                              </th>
                              <th className='text-left py-4 px-4'>
                                Parent Team
                              </th>
                              <th className='text-left py-4 px-4'>
                                Key Skills
                              </th>
                              <th className='text-left py-4 px-4'>Tenure</th>
                              <th className='text-right py-4 px-8 w-16' />
                            </tr>
                          </thead>
                          <tbody className='divide-y divide-border/30'>
                            {team.employees?.map((m, idx) => {
                              const emp = employeeByUserId.get(m.user_id)
                              const matchedUser = m.user_email
                                ? userByEmail.get(m.user_email.toLowerCase())
                                : undefined
                              const isLeader =
                                leaderUserId != null &&
                                matchedUser?.id === leaderUserId
                              return (
                                <MemberRow
                                  key={`emp-${m.user_id}`}
                                  index={idx}
                                  name={m.user_name}
                                  email={m.user_email}
                                  isLeader={isLeader}
                                  role={matchedUser?.role_name ?? null}
                                  sourceTeam={emp?.team ?? null}
                                  skills={emp?.skills ?? []}
                                  joined={emp?.date_joined ?? null}
                                  disabled={isRemoving}
                                  onRemove={() =>
                                    handleRemoveEmployee(m.user_id)
                                  }
                                />
                              )
                            })}
                            {team.users?.map((u, idx) => {
                              const isLeader = leaderUserId === u.id
                              return (
                                <MemberRow
                                  key={`user-${u.id}`}
                                  index={idx + (team.employees?.length ?? 0)}
                                  name={u.name}
                                  email={u.email}
                                  isLeader={isLeader}
                                  role={null}
                                  sourceTeam={null}
                                  skills={[]}
                                  joined={null}
                                  disabled={isRemoving}
                                  onRemove={() => handleRemoveUser(u.id)}
                                />
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </DialogContent>
      </Dialog>

      <TeamFormDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        team={editTeam}
      />
    </>
  )
}

interface StatCardProps {
  icon: ReactNode
  label: string
  value: number
  tint: string
}

const StatCard = ({ icon, label, value, tint }: StatCardProps) => (
  <div
    className={cn(
      'rounded-3xl border bg-card p-6 shadow-sm flex items-center gap-5 group hover:shadow-md transition-all duration-300',
      tint.split(' ').slice(2).join(' ')
    )}
  >
    <div
      className={cn(
        'h-14 w-14 rounded-2xl flex items-center justify-center shrink-0 shadow-inner group-hover:scale-110 transition-transform duration-500',
        tint.split(' ').slice(0, 2).join(' ')
      )}
    >
      {icon}
    </div>
    <div className='min-w-0'>
      <p className='text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-70 mb-0.5'>
        {label}
      </p>
      <p className='text-3xl font-black text-foreground leading-none tracking-tighter'>
        {value}
      </p>
    </div>
  </div>
)

interface MemberRowProps {
  index: number
  name: string
  email: string | null
  isLeader: boolean
  role: string | null
  sourceTeam: string | null
  skills: string[]
  joined: string | null
  disabled: boolean
  onRemove: () => void
}

const MemberRow = ({
  index,
  name,
  email,
  isLeader,
  role,
  sourceTeam,
  skills,
  joined,
  disabled,
  onRemove,
}: MemberRowProps) => {
  const initials = initialsFor(name)
  const color = avatarColor(name)
  let joinedLabel = '—'
  if (joined) {
    const d = new Date(joined)
    if (!Number.isNaN(d.getTime())) joinedLabel = format(d, 'MMM yyyy')
  }

  const rowVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { delay: index * 0.05, duration: 0.3 },
    },
  }

  return (
    <motion.tr
      variants={rowVariants}
      className='group hover:bg-muted/30 transition-all duration-200'
    >
      <td className='py-4 px-8'>
        <div className='flex items-center gap-4 min-w-0'>
          <div
            className={cn(
              'h-11 w-11 rounded-2xl flex items-center justify-center text-xs font-black shrink-0 shadow-sm border-2 border-white/10 group-hover:rotate-6 transition-all duration-300',
              color
            )}
          >
            {initials}
          </div>
          <div className='min-w-0'>
            <div className='flex items-center gap-2'>
              <span className='font-bold text-foreground text-sm tracking-tight truncate'>
                {name}
              </span>
              {isLeader && (
                <span className='inline-flex items-center gap-1 text-[8px] font-black uppercase tracking-tighter px-1.5 py-0.5 rounded-lg bg-amber-500/10 text-amber-600 border border-amber-500/20 shadow-sm'>
                  <Crown className='w-2.5 h-2.5 fill-amber-500/20' />
                  Leader
                </span>
              )}
            </div>
            {email && (
              <p className='text-[10px] text-muted-foreground font-medium truncate mt-0.5'>
                {email}
              </p>
            )}
          </div>
        </div>
      </td>
      <td className='py-4 px-4'>
        {role ? (
          <div className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-blue-500/5 border border-blue-500/10 text-blue-600 text-[10px] font-black uppercase tracking-tighter'>
            <ShieldCheck className='w-3 h-3' />
            {role}
          </div>
        ) : (
          '—'
        )}
      </td>
      <td className='py-4 px-4'>
        {sourceTeam ? (
          <span className='text-[10px] font-black uppercase tracking-tighter px-2.5 py-1 rounded-xl text-amber-600 bg-amber-500/5 border border-amber-500/10'>
            {sourceTeam}
          </span>
        ) : (
          <span className='text-xs text-muted-foreground/50'>—</span>
        )}
      </td>
      <td className='py-4 px-4'>
        {skills.length > 0 ? (
          <div className='flex flex-wrap gap-1.5 max-w-[280px]'>
            {skills.slice(0, 3).map(s => (
              <span
                key={s}
                className='text-[10px] px-2 py-0.5 rounded-lg bg-card text-foreground border border-border font-bold'
              >
                {s}
              </span>
            ))}
            {skills.length > 3 && (
              <span className='text-[10px] px-2 py-0.5 rounded-lg bg-muted text-muted-foreground/60 font-bold'>
                +{skills.length - 3}
              </span>
            )}
          </div>
        ) : (
          <span className='text-xs text-muted-foreground/50'>—</span>
        )}
      </td>
      <td className='py-4 px-4'>
        <div className='flex items-center gap-2 text-xs text-muted-foreground font-bold'>
          <CalendarDays className='w-3.5 h-3.5 opacity-50' />
          {joinedLabel}
        </div>
      </td>
      <td className='py-4 px-8 text-right'>
        <Button
          variant='ghost'
          size='icon'
          onClick={onRemove}
          disabled={disabled}
          className='opacity-0 group-hover:opacity-100 h-9 w-9 rounded-xl text-muted-foreground hover:text-destructive hover:bg-destructive/5 transition-all duration-300'
        >
          <Trash2 className='w-4.5 h-4.5' />
        </Button>
      </td>
    </motion.tr>
  )
}

interface AddMembersPanelProps {
  teamMemberIds: Set<number>
  employees: Employee[]
  onAdd: (userId: number) => Promise<void> | void
  isAssigning: boolean
  onDone: () => void
  memberCount: number
}

const AddMembersPanel = ({
  teamMemberIds,
  employees,
  onAdd,
  isAssigning,
  onDone,
  memberCount,
}: AddMembersPanelProps) => {
  const [search, setSearch] = useState('')
  const [pendingId, setPendingId] = useState<number | null>(null)

  const available = useMemo(() => {
    return employees.filter(e => !teamMemberIds.has(e.user_id))
  }, [employees, teamMemberIds])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return available
    return available.filter(
      e =>
        e.user_name?.toLowerCase().includes(q) ||
        e.user_email?.toLowerCase().includes(q) ||
        e.team?.toLowerCase().includes(q)
    )
  }, [available, search])

  const handleAdd = async (userId: number) => {
    setPendingId(userId)
    try {
      await onAdd(userId)
    } finally {
      setPendingId(null)
    }
  }

  return (
    <div className='rounded-[2.5rem] border border-border/50 bg-card shadow-2xl overflow-hidden'>
      <div className='flex items-center justify-between p-8 border-b border-border/50 bg-muted/20'>
        <div className='flex items-center gap-4'>
          <div className='h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary shadow-inner'>
            <UserPlus className='w-6 h-6' />
          </div>
          <div>
            <h3 className='text-xl font-black tracking-tight text-foreground'>
              Onboarding Center
            </h3>
            <p className='text-xs text-muted-foreground font-bold uppercase tracking-widest mt-0.5'>
              {memberCount} Current Members
            </p>
          </div>
        </div>
        <Button
          type='button'
          onClick={onDone}
          className='h-12 px-6 bg-foreground text-background hover:bg-foreground/90 transition-all duration-300 rounded-2xl font-bold flex items-center gap-2.5'
        >
          <Check className='w-4 h-4' />
          Finalize List
        </Button>
      </div>

      <div className='p-8 border-b border-border/50 bg-muted/5'>
        <div className='relative group'>
          <Search className='absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground group-focus-within:text-primary transition-colors' />
          <Input
            autoFocus
            placeholder='Search available resources by name, email, or team...'
            value={search}
            onChange={e => setSearch(e.target.value)}
            className='pl-12 h-14 bg-background border-border/50 focus-visible:ring-primary focus-visible:border-primary transition-all rounded-[1.25rem] shadow-sm text-base'
          />
        </div>
        <div className='flex justify-between mt-4 px-2'>
          <p className='text-[11px] font-black uppercase tracking-[0.2em] text-muted-foreground/60'>
            Showing {filtered.length} Matching Results
          </p>
          <p className='text-[11px] font-black uppercase tracking-[0.2em] text-primary/60'>
            {available.length} Total Available
          </p>
        </div>
      </div>

      <div className='p-8  max-h-[480px] overflow-y-auto'>
        {filtered.length === 0 ? (
          <div className='py-24 text-center'>
            <div className='inline-flex h-20 w-20 items-center justify-center rounded-[2rem] bg-muted/50 text-muted-foreground/30 mb-6'>
              <Search className='w-10 h-10' />
            </div>
            <p className='text-lg font-black text-foreground'>
              No matches found
            </p>
            <p className='text-sm text-muted-foreground max-w-xs mx-auto mt-2'>
              Adjust your search parameters or check if all employees are
              already onboarded.
            </p>
          </div>
        ) : (
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            {filtered.map((e, idx) => {
              const initials = initialsFor(e.user_name)
              const color = avatarColor(e.user_name)
              return (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.02 }}
                  key={e.user_id}
                  className='flex items-center gap-4 p-4 rounded-[1.5rem] border border-border/50 bg-card hover:border-primary/30 hover:shadow-lg transition-all duration-300 group'
                >
                  <div
                    className={cn(
                      'h-12 w-12 rounded-2xl flex items-center justify-center text-xs font-black shrink-0 shadow-sm border-2 border-white/10 group-hover:scale-110 transition-transform',
                      color
                    )}
                  >
                    {initials}
                  </div>
                  <div className='flex-1 min-w-0'>
                    <p className='text-sm font-black text-foreground truncate'>
                      {e.user_name}
                    </p>
                    <p className='text-[10px] text-muted-foreground font-bold uppercase tracking-widest mt-0.5 truncate'>
                      {e.team || 'Unassigned'}
                    </p>
                  </div>
                  <Button
                    type='button'
                    size='sm'
                    disabled={isAssigning && pendingId === e.user_id}
                    onClick={() => handleAdd(e.user_id)}
                    className='h-10 px-4 rounded-xl bg-primary/10 text-primary hover:bg-primary hover:text-primary-foreground font-bold text-xs gap-2 transition-all shadow-sm'
                  >
                    {isAssigning && pendingId === e.user_id ? (
                      <Loader2 className='w-3.5 h-3.5 animate-spin' />
                    ) : (
                      <PlusCircle className='w-3.5 h-3.5' />
                    )}
                    Onboard
                  </Button>
                </motion.div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default TeamDetailDialog
